import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Calendar, Clock, Play, Pause, Trash2, 
  ShoppingCart, Edit, Plus, Package, Sparkles, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const dayNames = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
};

const frequencyNames = {
  weekly: 'Semanal', daily: 'Diario', biweekly: 'Quincenal'
};

export default function RecurringOrdersSettings() {
  const { user, loading: authLoading } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  
  const [recurringOrders, setRecurringOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    frequency: 'weekly',
    day_of_week: 'monday',
    delivery_time: '12:00'
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      return;
    }
    
    if (user) {
      fetchRecurringOrders();
      fetchProducts();
    }
  }, [user, authLoading, navigate]);

  const fetchRecurringOrders = async () => {
    try {
      const res = await fetch(`${API}/recurring-orders`, { credentials: 'include' });
      if (res.ok) {
        setRecurringOrders(await res.json());
      }
    } catch (error) {
      console.error('Error fetching recurring orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API}/products`);
      if (res.ok) {
        setProducts(await res.json());
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const toggleOrder = async (orderId, currentActive) => {
    try {
      const res = await fetch(`${API}/recurring-orders/${orderId}/toggle`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (res.ok) {
        setRecurringOrders(prev => 
          prev.map(o => o.recurring_id === orderId ? {...o, active: !currentActive} : o)
        );
        toast.success(currentActive ? 'Pedido pausado' : 'Pedido activado');
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('¿Eliminar este pedido recurrente?')) return;
    
    try {
      const res = await fetch(`${API}/recurring-orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        setRecurringOrders(prev => prev.filter(o => o.recurring_id !== orderId));
        toast.success('Pedido eliminado');
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const orderNow = async (order) => {
    try {
      const res = await fetch(`${API}/recurring-orders/${order.recurring_id}/order-now`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        // Add all items to cart
        data.items.forEach(item => {
          const product = products.find(p => p.product_id === item.product_id);
          if (product) {
            addItem(product, item.quantity);
          }
        });
        toast.success('Productos agregados al carrito');
        navigate('/cart');
      }
    } catch (error) {
      toast.error('Error al agregar al carrito');
    }
  };

  const openEditDialog = (order) => {
    setEditingOrder(order);
    setEditForm({
      name: order.name,
      frequency: order.frequency,
      day_of_week: order.day_of_week || 'monday',
      delivery_time: order.delivery_time || '12:00'
    });
    setEditDialogOpen(true);
  };

  const saveEdit = async () => {
    if (!editingOrder) return;
    
    try {
      const res = await fetch(`${API}/recurring-orders/${editingOrder.recurring_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...editForm,
          items: editingOrder.items.map(i => ({ product_id: i.product_id, quantity: i.quantity }))
        })
      });
      
      if (res.ok) {
        toast.success('Pedido actualizado');
        setEditDialogOpen(false);
        fetchRecurringOrders();
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="recurring-orders-settings">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/settings')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-stone-900">Mis Pedidos Recurrentes</h1>
              <p className="text-stone-500">Administra tus pedidos automáticos semanales</p>
            </div>
            <Button 
              onClick={() => navigate('/menu')}
              className="bg-emerald-700 hover:bg-emerald-800 rounded-full"
              data-testid="create-new-recurring-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear nuevo
            </Button>
          </div>

          {/* Info Card */}
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 mb-8 border border-purple-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900 mb-1">¿Cómo funcionan los pedidos recurrentes?</h3>
                <p className="text-sm text-purple-700">
                  Los pedidos recurrentes te permiten programar tu comida favorita de forma automática. 
                  Usa el chat con el Nutricionista IA y dile algo como "Quiero lo mismo todos los martes" 
                  para crear nuevos pedidos.
                </p>
              </div>
            </div>
          </div>

          {/* Orders List */}
          {recurringOrders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center" data-testid="empty-state">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-stone-400" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2">No tienes pedidos recurrentes</h3>
              <p className="text-stone-500 mb-6 max-w-sm mx-auto">
                Crea tu primer pedido recurrente usando el chat con el Nutricionista IA
              </p>
              <Button 
                onClick={() => navigate('/menu')}
                className="bg-emerald-700 hover:bg-emerald-800 rounded-full"
                data-testid="empty-state-goto-menu"
              >
                Ir al menú
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {recurringOrders.map(order => (
                <div 
                  key={order.recurring_id} 
                  className={`bg-white rounded-2xl border ${order.active ? 'border-emerald-200' : 'border-stone-200'} overflow-hidden transition-all`}
                  data-testid={`recurring-order-${order.recurring_id}`}
                >
                  {/* Header */}
                  <div className={`px-6 py-4 flex items-center justify-between ${order.active ? 'bg-emerald-50' : 'bg-stone-50'}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${order.active ? 'bg-emerald-500' : 'bg-stone-300'}`}>
                        <RefreshCw className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900">{order.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-stone-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {dayNames[order.day_of_week] || 'No definido'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {order.delivery_time || '12:00'}
                          </span>
                          <Badge variant="secondary" className={order.active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}>
                            {frequencyNames[order.frequency] || 'Semanal'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-stone-500">{order.active ? 'Activo' : 'Pausado'}</span>
                        <Switch 
                          checked={order.active} 
                          onCheckedChange={() => toggleOrder(order.recurring_id, order.active)}
                          data-testid={`toggle-${order.recurring_id}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="px-6 py-4">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                      {order.items?.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 bg-stone-50 rounded-xl p-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-900 text-sm truncate">{item.name}</p>
                            <p className="text-xs text-stone-500">${item.price?.toFixed(2)} x {item.quantity}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      <div>
                        <span className="text-sm text-stone-500">Total por pedido:</span>
                        <span className="ml-2 text-lg font-bold text-emerald-700">${order.total?.toFixed(2)} MXN</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="rounded-xl"
                          onClick={() => openEditDialog(order)}
                          data-testid={`edit-${order.recurring_id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteOrder(order.recurring_id)}
                          data-testid={`delete-${order.recurring_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                          onClick={() => orderNow(order)}
                          data-testid={`order-now-${order.recurring_id}`}
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Pedir ahora
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Edit Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-md" data-testid="edit-dialog">
              <DialogHeader>
                <DialogTitle>Editar Pedido Recurrente</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-2">Nombre</label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    placeholder="Mi almuerzo saludable"
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-2">Frecuencia</label>
                  <Select value={editForm.frequency} onValueChange={(v) => setEditForm({...editForm, frequency: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Diario</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="biweekly">Quincenal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-2">Día de la semana</label>
                  <Select value={editForm.day_of_week} onValueChange={(v) => setEditForm({...editForm, day_of_week: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monday">Lunes</SelectItem>
                      <SelectItem value="tuesday">Martes</SelectItem>
                      <SelectItem value="wednesday">Miércoles</SelectItem>
                      <SelectItem value="thursday">Jueves</SelectItem>
                      <SelectItem value="friday">Viernes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-2">Hora de entrega</label>
                  <Select value={editForm.delivery_time} onValueChange={(v) => setEditForm({...editForm, delivery_time: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="08:00">8:00 AM</SelectItem>
                      <SelectItem value="09:00">9:00 AM</SelectItem>
                      <SelectItem value="10:00">10:00 AM</SelectItem>
                      <SelectItem value="11:00">11:00 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM</SelectItem>
                      <SelectItem value="13:00">1:00 PM</SelectItem>
                      <SelectItem value="14:00">2:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button className="w-full bg-emerald-700 hover:bg-emerald-800" onClick={saveEdit} data-testid="save-edit-btn">
                  Guardar cambios
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
